import styled from '@emotion/styled';
import {
  ApexChartWrapper,
  Typography,
  useGlimmerTheme,
} from '@innovamat/glimmer-components';
import type {
  PercentileThreshold,
  StudentIntervention,
} from '@innovamat/glow-api-client';
import type { ApexOptions } from 'apexcharts';
import { useTranslation } from 'react-i18next';
import { createCatchUpChartTooltip } from './catch-up-chart-tooltip';
import {
  calculateAnnotationPosition,
  calculateChartSeries,
  calculateCurrentWeekIndex,
  calculateSeriesData,
  calculateYAxisMax,
  generateDiscreteMarkers,
  generatePercentileSeries,
  generateWeekCategories,
  getChartColors,
} from './utils/chart-utils';
import { interpolateNullValues } from './utils/interpolate-null-values';

type Props = {
  currentWeek?: number;
  maxWeeks?: number;
  percentiles: PercentileThreshold[];
  students: StudentIntervention[];
};

const ChartWrapper = styled.div<{
  currentWeekIndex: number;
  percentile30Indices: number[];
}>`
  width: 100%;
  margin: 24px 0;

  .apexcharts-xaxis {
    .apexcharts-xaxis-label {
      &:nth-child(${({ currentWeekIndex }) => currentWeekIndex + 1}) {
        font-weight: bold;
      }
    }
  }
  .apexcharts-xaxistooltip {
    display: none !important;
  }
  .apexcharts-legend-series[seriesname='Percentile30'] {
    display: none !important;
  }

  .apexcharts-grid-row line,
  .apexcharts-gridlines-horizontal line,
  .apexcharts-gridline {
    stroke-opacity: 0.5 !important;
  }

  ${({ percentile30Indices }) =>
    percentile30Indices
      .map(
        (index) => `
    .apexcharts-series[data\\:realIndex="${index}"],
    .apexcharts-series[data\\:realIndex="${index}"] path,
    .apexcharts-series[data\\:realIndex="${index}"] circle {
      opacity: 1 !important;
    }
  `
      )
      .join('')}
`;

const StyledTitle = styled(Typography.Body3)`
  margin-left: 15px;
  color: ${({ theme }) =>
    theme.tokens.color.alias.cm.text['text-subtle'].value};
`;

function CatchUpChart({
  currentWeek,
  maxWeeks,
  percentiles,
  students,
}: Props): JSX.Element {
  const theme = useGlimmerTheme();
  const { t } = useTranslation();

  const currentWeekIndex = calculateCurrentWeekIndex(currentWeek);
  const seriesData = calculateSeriesData(
    students,
    interpolateNullValues,
    currentWeekIndex
  );
  const chartSeries = calculateChartSeries(seriesData, maxWeeks, students);
  const weekCategories = generateWeekCategories(
    maxWeeks,
    students,
    currentWeek,
    t
  );
  const yAxisMaxRaw = calculateYAxisMax(students, percentiles);
  const yAxisMax = Math.ceil(yAxisMaxRaw / 2) * 2;
  const discreteMarkers = generateDiscreteMarkers(
    seriesData,
    currentWeekIndex,
    theme
  );

  const totalWeeks = maxWeeks || students[0]?.weeks.length || 0;
  const CHART_HEIGHT = `${yAxisMaxRaw * 16}px`;
  const CHART_WIDTH = 1000;
  const ANNOTATION_WIDTH = CHART_WIDTH / totalWeeks;

  const annotationPosition = calculateAnnotationPosition(
    currentWeek,
    totalWeeks,
    currentWeekIndex,
    CHART_WIDTH,
    ANNOTATION_WIDTH
  );

  const {
    series: percentileSeries,
    seriesIndices: percentile30SeriesIndices,
    discreteMarkers: percentileDiscreteMarkers,
  } = generatePercentileSeries(percentiles, totalWeeks, chartSeries.length);

  const finalChartSeries = [...chartSeries, ...percentileSeries];
  const finalDiscreteMarkers = [
    ...discreteMarkers,
    ...percentileDiscreteMarkers,
  ];

  const chartOptions: ApexOptions = {
    chart: {
      animations: {
        enabled: false,
      },
      type: 'line',
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
      events: {
        markerClick: () => {
          return false;
        },
      },
    },
    states: {
      active: {
        filter: {
          type: 'none',
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'straight',
      width: 1.3,
      dashArray: Array(finalChartSeries.length)
        .fill(0)
        .map((_, index) => (percentile30SeriesIndices.includes(index) ? 2 : 0)),
    },
    markers: {
      size: 5,
      hover: {
        size: 7,
      },
      discrete: finalDiscreteMarkers,
    },
    grid: {
      row: {
        colors: ['transparent', 'transparent'],
      },
    },
    xaxis: {
      categories: weekCategories,
      tooltip: {
        enabled: false,
      },
      max: maxWeeks,
      crosshairs: {
        show: false,
      },
    },
    yaxis: {
      min: 0,
      max: yAxisMax,
      decimalsInFloat: 0,
      forceNiceScale: false,
      tickAmount: yAxisMax / 2,
      labels: {
        formatter: (value: number) => {
          const roundedValue = Math.round(value);
          return roundedValue % 2 === 0 ? roundedValue.toString() : '';
        },
      },
      crosshairs: {
        show: false,
      },
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'left',
      formatter: (seriesName: string) => {
        const parts = seriesName.split('::');
        return parts[0] || seriesName;
      },
      onItemClick: {
        toggleDataSeries: false,
      },
      onItemHover: {
        highlightDataSeries: true,
      },
    },
    tooltip: {
      shared: false,
      intersect: true,
      custom: createCatchUpChartTooltip(students),
    },
    annotations: {
      xaxis: annotationPosition
        ? [
            {
              x: annotationPosition.x,
              x2: annotationPosition.x2,
              strokeDashArray: 0,
              borderColor: theme.tokens.color.global.neutral.neutral100.value,
              borderWidth: 0,
            },
          ]
        : [],
      yaxis: percentiles.map((percentile) => ({
        y: percentile.finalValue,
        borderColor: 'transparent',
        label: {
          borderColor: 'transparent',
          style: {
            background: 'transparent',
          },
          text:
            percentiles.length === 1
              ? t('reports.catch-up.fluencyGraphic.percentile30')
              : t(
                  `reports.catch-up.fluencyGraphic.percentile30${percentile.courseOrder}Years`
                ),
        },
      })),
    },
    colors: getChartColors(
      theme,
      finalChartSeries.length,
      percentile30SeriesIndices
    ),
  };

  return (
    <ChartWrapper
      currentWeekIndex={currentWeekIndex >= 0 ? currentWeekIndex : 0}
      percentile30Indices={percentile30SeriesIndices}
    >
      <StyledTitle>{t('reports.catch-up.fluencyGraphic.subtitle')}</StyledTitle>
      <ApexChartWrapper
        id="catch-up-progress-chart"
        options={chartOptions}
        series={finalChartSeries}
        type="line"
        height={CHART_HEIGHT}
        width={CHART_WIDTH}
        data-testid="catch-up-chart"
      />
    </ChartWrapper>
  );
}

export { CatchUpChart };
